import adminApiClient from "@/lib/api/admin-api-client";
import type {
  AccessRequestStatus,
  AccessScope,
  ApiPlatform,
  RequesterType,
} from "@/lib/api/access-requests";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export type AdminActor = {
  id: string;
  name: string;
};

export type AdminAccessRequest = {
  id: string;
  portal_user_id: string;
  api_platform: ApiPlatform;
  requester_type: RequesterType;
  intended_use_case: string;
  access_scope?: AccessScope | null;
  location_ids: string[];
  namespaces: string[];
  expected_daily_volume: ExpectedDailyVolume;
  integrations: string[];
  status: AccessRequestStatus;
  contact_name: string;
  email: string;
  company_name: string;
  reviewed_by?: string | null;
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  /** Applicant-visible reason when status is rejected. */
  rejection_reason?: string | null;
  /** Present on admin detail / upsert responses; omitted from list. */
  internal_note?: string | null;
  internal_note_created_by?: AdminActor | null;
  internal_note_created_at?: string | null;
  internal_note_updated_by?: AdminActor | null;
  internal_note_updated_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type AdminAccessRequestSummary = {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
};

export type AdminListAccessRequestsResponse = {
  requests: AdminAccessRequest[];
  summary: AdminAccessRequestSummary;
};

export type AdminListAccessRequestsParams = {
  status?: AccessRequestStatus;
  requester_type?: RequesterType;
  q?: string;
};

const ADMIN_ACCESS_REQUESTS_PATH = "/api/v1/admin/access-requests";

export async function listAdminAccessRequests(
  params: AdminListAccessRequestsParams = {},
): Promise<AdminListAccessRequestsResponse> {
  const { data } = await adminApiClient.get<AdminListAccessRequestsResponse>(
    ADMIN_ACCESS_REQUESTS_PATH,
    {
      params: {
        ...(params.status ? { status: params.status } : {}),
        ...(params.requester_type
          ? { requester_type: params.requester_type }
          : {}),
        ...(params.q?.trim() ? { q: params.q.trim() } : {}),
      },
    },
  );
  return data;
}

export async function getAdminAccessRequest(
  id: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.get<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}`,
  );
  return data;
}

export async function upsertAdminAccessRequestInternalNote(
  id: string,
  internalNote: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.put<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}/internal-note`,
    { internal_note: internalNote },
  );
  return data;
}

export type AdminReviewStatus = Extract<
  AccessRequestStatus,
  "approved" | "rejected"
>;

export async function reviewAdminAccessRequest(
  id: string,
  status: AdminReviewStatus,
  rejectionReason?: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.post<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}/review`,
    {
      status,
      ...(status === "rejected" && rejectionReason
        ? { rejection_reason: rejectionReason }
        : {}),
    },
  );
  return data;
}
